import { useLazyQuery, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import HelpOutline from "@mui/icons-material/HelpOutline";
import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem, GridRowParams } from "@mui/x-data-grid-premium";
import {
  DateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DateShortcutChips } from "components/DateShortcutChips";
import { ExportDataButton } from "components/ExportDataButton";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import { useProductAnnouncement } from "components/ProductAnnouncement/useProductAnnouncement";
import { DueDateCell } from "components/Submission/DueDateCell";
import { FacilitiesCell } from "components/Submission/FacilitiesCell";
import { ALL_STATUSES, US_STATES } from "components/Submission/helpers/constants";
import { StaffAssigneeCell } from "components/Submission/StaffAssigneeCell";
// V2 OmnisearchDataGrid is used via GridSection
import { SubmissionReviewerCell } from "components/Submission/SubmissionReviewerCell";
import { SubmissionStatusCell } from "components/Submission/SubmissionStatusCell";
import { extractKeyValuePairs } from "encamp-omnisearch/src/utils/util";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { runsInteractiveStepper } from "encamp-shared/src/submissions/wasteSubmissionForms";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  ProductAnnouncementKey,
  RegulatoryStatusOption,
  SignatureType,
  SubmissionEncampStatus,
  SubmissionStatus,
  SubmissionType,
  SubmissionVirtualEventsQuery,
} from "generated-graphql/graphql";
import {
  GridSelectionProvider,
  useGridSelectionApiRef,
  useOmnisearchState,
} from "hooks/omnisearch-v2";
import { useAutoFlex } from "hooks/useAutoFlex";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCreateStepperSubmission } from "hooks/useCreateStepperSubmission";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { SUBMISSION_VIRTUAL_EVENTS_QUERY } from "queries/submissions";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getDateRangeShortcuts } from "util/dateRangeShortcuts";
import { getDateLikeIsoDate } from "util/dates";
import { stepperFlagForSubmission } from "util/wasteStepperFlags";

// Re-export Row type for backwards compatibility
import type { Row } from "./types";

import { mergeDefaultAndOmnisearch } from "../Manifests/mergeDefaultAndOmnisearch";
import { WasteStepperAnnouncement } from "./Announcement/WasteStepperAnnouncement";
import { BulkCreateNewSubmissionsDialog } from "./BulkCreateNewSubmissionsDialog";
import { BulkDownloadSubmissionsButton } from "./BulkDownloadSubmissionsButton";
import { CreateStepperArgs, CreateSubmissionTypeDialog } from "./CreateSubmissionTypeDialog";
import { GeneratorStatusFilter } from "./filters/GeneratorStatusFilter";
import { StateFilter } from "./filters/StateFilter";
import { StatusFilter } from "./filters/StatusFilter";
import { TagsFilter } from "./filters/TagsFilter";
import { GridSection } from "./GridSection";
import { getOmnisearchSessionKey, getOmnisearchStorageKey } from "./navigation";
// SelectionContext removed - row selection handled by GridSelectionProvider,
// chart selection derived from omnisearch
import {
  WasteSubmissionsOverviewFilter,
  WasteSubmissionsOverviewProvider,
} from "./Overview/WasteSubmissionsOverviewContext";
import { WasteSubmissionStatsProvider, useWasteSubmissionStats } from "./StatsContext";
import { StatsSection } from "./StatsSection";
import { SubmissionBulkActionsMenu } from "./SubmissionBulkActionsMenu";

// Stable empty array constant for use in memos to prevent reference changes
const EMPTY_ARRAY: string[] = [];

const ALL_GENERATOR_STATUSES = [
  RegulatoryStatusOption.LargeQuantityGenerator,
  RegulatoryStatusOption.SmallQuantityGenerator,
  RegulatoryStatusOption.VerySmallQuantityGenerator,
  RegulatoryStatusOption.None,
];

const INITIAL_COLUMN_VISIBILITY_MODEL = {
  notes: false,
  waitingOn: false,
};

const MOBILE_COLUMN_VISIBILITY_MODEL = {
  notes: false,
  waitingOn: false,
  facilities: false,
  tags: false,
};

// Wrapper for ExportDataButton that reads omnisearch lazily at click time
// This prevents commandButtons from being recreated when omnisearch changes
const StableExportButton = memo(function StableExportButton({
  tenantId,
  defaultSearch,
  getOmnisearch,
}: {
  tenantId: string | undefined;
  defaultSearch: string;
  getOmnisearch: () => string;
}) {
  // Layer defaultSearch under omnisearch — same merge as the grid query (V1 OmnisearchDataDisplay).
  const filter = useMemo(
    () => ({
      get search() {
        return mergeDefaultAndOmnisearch(defaultSearch, getOmnisearch());
      },
    }),
    [defaultSearch, getOmnisearch]
  );

  return (
    <ExportDataButton
      fileName={`waste-submissions-${tenantId}`}
      tenantId={tenantId}
      filter={filter}
      exportType="wasteSubmissions"
      dialog={true}
    />
  );
});

// Stable constants for useAutoFlex (must be module-level for referential stability)
const AUTO_FLEX_IMPORTANT_COLUMNS = {
  deadline: 0.1,
  title: 0.25,
  facilities: 0.1,
  epaIds: 0.1,
  "submission.status": 0.1,
  submissionType: 0.1,
  "submission.title": 0.1,
};
const AUTO_FLEX_ALL_COLUMNS = [
  "facilities",
  "epaIds",
  "submissionType",
  "title",
  "documents",
  "reviewers",
  "signer",
  "staffAssignee",
  "submission.status",
  "submission.title",
];

// Tags query for filtering
const FACILITY_TAGS_QUERY = gql(`
  query SubmissionsFacilityTags(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tags(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

// Forward-looking shortcuts for the due-date filter.
const datePickerShortcuts = getDateRangeShortcuts("FUTURE");

// Default = All statuses except completed (Filing Complete, Not Reporting)
// Using explicit string values to ensure consistent comparison with Object.keys()
const DEFAULT_STATUSES: SubmissionStatus[] = [
  SubmissionStatus.NotStarted,
  SubmissionStatus.InProgress,
  SubmissionStatus.EncampInitialReview,
  SubmissionStatus.InReview,
  SubmissionStatus.ProcessingSubmission,
  SubmissionStatus.FilingComplete,
];

// Default status values as strings for filtering - ensures exact match with Object.keys() output
const DEFAULT_STATUS_SET = new Set<string>(DEFAULT_STATUSES.map(String));

// Default date filter = 30 days back through 60 days forward (lookback keeps recently-past-due visible)
const DEFAULT_DUE_DATE_START = DateTime.now().minus({ days: 30 }).startOf("day").toISODate();
const DEFAULT_DUE_DATE_END = DateTime.now().plus({ days: 60 }).startOf("day").toISODate();
const DEFAULT_DUE_DATE = `${DEFAULT_DUE_DATE_START}..${DEFAULT_DUE_DATE_END}`;

// Default chart filter - ensures charts use the same defaults as the data grid
// This is a module-level constant since all values are derived from other constants
// IMPORTANT: Use UTC zone when parsing date strings to match how the datagrid resolver
// parses dates. Without this, local timezone interpretation causes events at midnight UTC
// to be excluded from charts but included in the datagrid.
const DEFAULT_CHART_FILTER: WasteSubmissionsOverviewFilter = {
  dueDateStart: DEFAULT_DUE_DATE_START
    ? (DateTime.fromISO(DEFAULT_DUE_DATE_START, { zone: "utc" }).toISO() ?? undefined)
    : undefined,
  dueDateEnd: DEFAULT_DUE_DATE_END
    ? (DateTime.fromISO(DEFAULT_DUE_DATE_END, { zone: "utc" }).toISO() ?? undefined)
    : undefined,
  statuses: DEFAULT_STATUSES,
};

// Helper to extract a key's value from an omnisearch string
function extractOmnisearchValue(omnisearch: string | null, key: string): string | undefined {
  if (!omnisearch) return undefined;
  const regex = new RegExp(`(?:^|\\s)${key}:(?:"([^"]+)"|([^\\s]+))`, "i");
  const match = omnisearch.match(regex);
  if (match) {
    return match[1] ?? match[2];
  }
  return undefined;
}

// Helper to compare arrays (order-independent)
function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

// Note: This component is wrapped with OmnisearchProvider in main.tsx router
// Provider order:
// 1. WasteSubmissionStatsProvider - manages collapse state
// 2. WasteSubmissionsOverviewProvider - fires chart queries BEFORE DataGrid renders
// 3. GridSelectionProvider - manages grid selection state
// Note: SelectionProvider removed - row selection handled by useGridSelection,
// chart selection derived from omnisearch
export function WasteSubmissions() {
  return (
    <WasteSubmissionStatsProvider>
      <WasteSubmissionsOverviewProvider defaultFilter={DEFAULT_CHART_FILTER}>
        <GridSelectionProvider>
          <WasteSubmissionsContent />
        </GridSelectionProvider>
      </WasteSubmissionsOverviewProvider>
    </WasteSubmissionStatsProvider>
  );
}

function WasteSubmissionsContent() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const theme = useTheme();

  const { isStaff, user } = useCurrentUser();
  const currentUserEmail = user?.email;
  const { statsOpen, handleToggle } = useWasteSubmissionStats();

  // Get apiRef from GridSelectionProvider context
  // Used only for clearing selection on filter change - NOT for subscribing
  // Selection subscription happens in SubmissionBulkActionsMenu via useGridSelection()
  const apiRef = useGridSelectionApiRef();
  // Chart selection is DERIVED from omnisearch, not stored separately.
  // A chart bar is "selected" when the filter is a single value matching that bar.
  // This eliminates the need for a separate context and the re-render cascade it caused.

  // Tags filter state - tags list for filter component
  const [allFacilityTags, setAllFacilityTags] = useState<Array<{ id: string; name: string }>>([]);
  const [facilityTagsPage, setFacilityTagsPage] = useState(0);
  const [isFetchingMoreFacilityTags, setIsFetchingMoreFacilityTags] = useState(false);

  // Fetch facility tags - use regular useQuery to avoid suspending the whole component
  // Always filter out internal tags on this page (for both staff and customers)
  const { data: tagsData } = useQuery(FACILITY_TAGS_QUERY, {
    variables: {
      search: `tenantId:${tenantId} isWasteFacilityTag:true`,
      page: 0,
      pageSize: 100,
      sort: [{ field: "name", sort: "asc" }],
    },
    skip: !tenantId,
  });

  // Lazy query for loading more tags
  const [fetchMoreFacilityTags, { data: moreTagsData, error: moreTagsError }] =
    useLazyQuery(FACILITY_TAGS_QUERY);

  // Handle lazy query completion (onCompleted is deprecated in Apollo Client 3.14+)
  useEffect(() => {
    if (moreTagsData?.tags?.items) {
      setAllFacilityTags((prev) => [...prev, ...moreTagsData.tags.items]);
      setIsFetchingMoreFacilityTags(false);
    }
  }, [moreTagsData]);

  // Handle lazy query error (onError is deprecated in Apollo Client 3.14+)
  useEffect(() => {
    if (moreTagsError) {
      setIsFetchingMoreFacilityTags(false);
    }
  }, [moreTagsError]);

  // Initialize tags list with initial data
  useEffect(() => {
    if (tagsData?.tags?.items) {
      setAllFacilityTags(tagsData.tags.items);
    }
  }, [tagsData]);

  const hasMoreFacilityTags = useMemo(() => {
    if (!tagsData?.tags?.count) return false;
    return allFacilityTags.length < tagsData.tags.count;
  }, [tagsData?.tags?.count, allFacilityTags.length]);

  const handleFacilityTagsScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const scrollThreshold = 50;
      const scrollDistance = target.scrollHeight - target.scrollTop - target.clientHeight;

      if (scrollDistance < scrollThreshold && hasMoreFacilityTags && !isFetchingMoreFacilityTags) {
        setIsFetchingMoreFacilityTags(true);
        const nextPage = facilityTagsPage + 1;
        setFacilityTagsPage(nextPage);

        fetchMoreFacilityTags({
          variables: {
            search: `tenantId:${tenantId} isWasteFacilityTag:true`,
            page: nextPage,
            pageSize: 100,
            sort: [{ field: "name", sort: "asc" }],
          },
        });
      }
    },
    [
      hasMoreFacilityTags,
      isFetchingMoreFacilityTags,
      facilityTagsPage,
      tenantId,
      fetchMoreFacilityTags,
    ]
  );

  // Needed by getRowHref: guided-submission links dispatch on the stepper flag
  // for the row's archetype.
  const { featureFlags } = useFeatureFlags();
  const stepperEnabled = featureFlags[FeatureFlagKey.WasteSubmissionStepper] === true;
  const documentStepperEnabled =
    featureFlags[FeatureFlagKey.WasteDocumentSubmissionStepper] === true;
  const anyStepperEnabled = stepperEnabled || documentStepperEnabled;

  const {
    phase: announcementPhase,
    shouldShow: announcementIsPending,
    dismissModal: dismissAnnouncementModal,
    clickPrimaryCta: clickAnnouncementPrimaryCta,
    reopenModal: reopenAnnouncementModal,
  } = useProductAnnouncement(ProductAnnouncementKey.WasteDocumentStepper_2026_08, {
    skip: !documentStepperEnabled,
  });

  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const { createStepperSubmission, loading: creatingStepper } = useCreateStepperSubmission();

  const getRowHref = useCallback(
    (row: Row): string | null => {
      const submissionId = row.submission?.id;

      // Virtual events come from recurring tasks (YEARLY, MONTHLY, etc.)
      // Materialized instances have frequency ONCE and their own submission record
      const isMaterializedInstance = row.frequency === "ONCE";

      if (!isMaterializedInstance) {
        if (!isStaff) {
          return null;
        }
        const dateOnly = getDateLikeIsoDate(String(row.deadline));
        if (!dateOnly) {
          return null;
        }
        return `/o/${tenantId}/waste/submissions/create?taskId=${row.taskId}&startDate=${dateOnly}`;
      }

      // Every submission opens in the stepper, for staff and customers alike,
      // regardless of status, once backfilled — routing is purely flag-driven:
      // the archetype (interactive vs document form, per its actual persisted
      // steps) picks which flag gates it. With that flag off it falls through
      // to the legacy routing below, matching the stepper shell's own redirect.
      if (
        featureFlags[stepperFlagForSubmission(row.submission?.usesInteractiveStepper ?? false)] ===
        true
      ) {
        return `/o/${tenantId}/waste/submissions/${submissionId}/stepper`;
      }

      if (!isStaff) {
        return `/o/${tenantId}/waste/submissions/${submissionId}`;
      }

      // Staff users: determine view mode based on submission status
      const status = row.submission?.status;
      const hasNoStaffReviewer = !row.submission?.reviewers?.some((r) => r.isStaff);
      const isUnassignedPeerReview =
        status === SubmissionStatus.EncampInitialReview &&
        row.submission?.peerReviewRequired === true &&
        hasNoStaffReviewer;

      if (isUnassignedPeerReview) {
        return `/o/${tenantId}/waste/submissions/${submissionId}/edit`;
      }

      const shouldViewReadOnly =
        status === SubmissionStatus.EncampInitialReview ||
        status === SubmissionStatus.InReview ||
        status === SubmissionStatus.FilingComplete ||
        status === SubmissionStatus.NotReporting;

      if (shouldViewReadOnly) {
        return `/o/${tenantId}/waste/submissions/${submissionId}`;
      }
      return `/o/${tenantId}/waste/submissions/${submissionId}/edit`;
    },
    [isStaff, tenantId, featureFlags]
  );

  const alerts = useAlerts();

  // A not-started task row's own submission (the recurring template's, set at
  // requirement-derivation time by createRRuleTaskAndSubmission) already
  // carries submissionType/submissionMethod — there's nothing left to ask the
  // user, so create it directly and drop into the stepper instead of routing
  // through the legacy create form.
  const handleRowClick = useCallback(
    async (row: Row) => {
      const isMaterializedInstance = row.frequency === "ONCE";
      const submissionType = row.submission?.submissionType;
      const submissionMethod = row.submission?.submissionMethod;
      const dateOnly = getDateLikeIsoDate(String(row.deadline));
      // No persisted steps yet, so the archetype it would be created as decides
      // which flag gates creating it directly. Without the interactive stepper —
      // or without a method — that's a document form.
      const flagOn =
        !!submissionType &&
        (runsInteractiveStepper(submissionType, submissionMethod, stepperEnabled) ||
          documentStepperEnabled);

      // `creatingStepper` blocks a double-click/rapid-repeat from firing a
      // second create while the first is still in flight — the server also
      // dedupes by virtualEventId, but this avoids the wasted round-trip.
      if (!isMaterializedInstance && isStaff && flagOn && dateOnly && !creatingStepper) {
        try {
          const id = await createStepperSubmission({
            submissionType,
            submissionMethod,
            facilityIds: (row.facilities ?? []).map((f) => f.id),
            title: row.title ?? "",
            dueDate: DateTime.fromISO(dateOnly),
            virtualEventId: `${row.taskId}_${dateOnly}`,
          });
          if (id) {
            navigate(`/o/${tenantId}/waste/submissions/${id}/stepper`);
          }
        } catch (error) {
          alerts.error("Failed to create the submission.", error);
        }
        return;
      }
      const href = getRowHref(row);
      if (href) {
        navigate(href);
      }
    },
    [
      getRowHref,
      navigate,
      isStaff,
      stepperEnabled,
      documentStepperEnabled,
      creatingStepper,
      createStepperSubmission,
      tenantId,
      alerts,
    ]
  );

  // With either guided flow enabled, creation opens the facility-first form
  // picker. Each flag enables its own filing methods.
  const handleAddSubmission = useCallback(() => {
    if (anyStepperEnabled) {
      setShowTypeDialog(true);
      return;
    }
    navigate(`/o/${tenantId}/waste/submissions/create`);
  }, [anyStepperEnabled, navigate, tenantId]);

  // Stepper path: create the data-entry submission directly and drop the user
  // into the stepper.
  const handleCreateStepper = useCallback(
    async (args: CreateStepperArgs) => {
      try {
        const id = await createStepperSubmission(args);
        if (id) {
          setShowTypeDialog(false);
          navigate(`/o/${tenantId}/waste/submissions/${id}/stepper`);
        }
      } catch (error) {
        alerts.error("Failed to create the submission.", error);
      }
    },
    [createStepperSubmission, navigate, tenantId, alerts]
  );

  const [showBulkCreateDialog, setShowBulkCreateDialog] = useState(false);
  const handleOpenBulkCreate = useCallback(() => {
    setShowBulkCreateDialog(true);
  }, []);

  const handleBulkCreateSuccess = useCallback(() => {
    const api = apiRef?.current;
    if (api) {
      api.setRowSelectionModel({ type: "include", ids: new Set() });
    }
    setShowBulkCreateDialog(false);
  }, [apiRef]);

  useBreadcrumb([{ label: "Waste", to: `/o/${tenantId}/waste` }, { label: "Submissions" }]);

  const omnisearchPersistenceKey = "submissions-list-view-v9";
  const autoFlex = useAutoFlex(1.0, AUTO_FLEX_IMPORTANT_COLUMNS, AUTO_FLEX_ALL_COLUMNS);

  // Memoize columns to prevent recreating on every render
  // This significantly improves DataGrid performance
  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      {
        field: "deadline",
        headerName: "Due Date",
        sortable: true,
        flex: autoFlex.get("deadline"),
        minWidth: 20,
        renderCell: ({ row }) => {
          return <DueDateCell dueDate={row.submission?.dueDate} deadline={row.deadline} />;
        },
      },
      {
        field: "facilities",
        headerName: "Facilities",
        flex: autoFlex.get("facilities"),
        minWidth: 20,
        sortable: true,
        renderCell({ row }) {
          const facilities = row.facilities ?? [];
          return <FacilitiesCell facilities={facilities} />;
        },
      },
      {
        field: "epaIds",
        headerName: "EPA ID",
        flex: autoFlex.get("epaIds"),
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) => {
          const epaIds = row.epaIds ?? [];
          if (epaIds.length === 0) {
            return "";
          }

          const epaIdsText = epaIds.join("; ");
          return (
            <Tooltip title={epaIdsText}>
              <CellText variant="body2" noWrap>
                {epaIdsText}
              </CellText>
            </Tooltip>
          );
        },
      },
      {
        field: "submissionType",
        headerName: "Submission Type",
        flex: autoFlex.get("submissionType"),
        minWidth: 100,
        sortable: true,
        renderCell: ({ row: { submission } }) => {
          return (
            <Tooltip title={submission?.submissionType}>
              <CellText variant="body2" noWrap>
                {submission?.submissionType ? prettyPrintEnumValue(submission.submissionType) : ""}
              </CellText>
            </Tooltip>
          );
        },
      },
      {
        field: "title",
        headerName: "Submission Title",
        flex: autoFlex.get("title"),
        minWidth: 100,
        sortable: true,
        renderCell: ({ row: { submission } }) => {
          return (
            <CellText variant="body2" noWrap>
              {submission?.title}
            </CellText>
          );
        },
      },
      {
        field: "notes",
        headerName: "Notes",
        flex: autoFlex.get("notes"),
        minWidth: 100,
        sortable: false,
        renderCell: ({ row }) => {
          const notes = row.submission?.notes;
          if (!notes) return "";
          return (
            <CellText variant="body2" noWrap>
              {notes}
            </CellText>
          );
        },
      },
      {
        field: "documents",
        headerName: "Attachment(s)",
        flex: autoFlex.get("documents"),
        minWidth: 60,
        sortable: false,
        renderCell: (params) => {
          const documents = params.row.submission?.documents || [];

          if (documents.length === 0) return "";
          const mappedDocuments = documents.map((doc) => doc.document.title).join(", ");
          return (
            <Tooltip title={mappedDocuments}>
              <CellText variant="body2">{mappedDocuments}</CellText>
            </Tooltip>
          );
        },
      },
      {
        field: "reviewers",
        headerName: "Reviewer(s)",
        flex: autoFlex.get("reviewers"),
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          const reviewers = params.row.submission?.reviewers || [];
          const reviewMode = params.row.submission?.reviewMode;
          const submissionStatus = params.row.submission?.status;

          if (reviewers.length === 0) {
            return null;
          }

          return (
            <SubmissionReviewerCell
              reviewers={reviewers}
              reviewMode={reviewMode}
              submissionStatus={submissionStatus}
              isStaff={isStaff}
              type="reviewer"
            />
          );
        },
      },
      {
        field: "signer",
        headerName: "Signer",
        flex: autoFlex.get("signer"),
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          const reviewers = params.row.submission?.reviewers || [];
          const reviewMode = params.row.submission?.reviewMode;
          const submissionStatus = params.row.submission?.status;
          const signers = reviewers.filter((r) => r.isSigner);
          const signatureType = params.row.submission?.signatureType;

          // Handle no signature required cases
          if (signatureType === SignatureType.None || signers.length === 0) {
            return (
              <CellText variant="body2" noWrap>
                No Signature Required
              </CellText>
            );
          }

          return (
            <SubmissionReviewerCell
              reviewers={reviewers}
              reviewMode={reviewMode}
              submissionStatus={submissionStatus}
              isStaff={isStaff}
              type="signer"
            />
          );
        },
      },
      ...(isStaff
        ? ([
            {
              field: "staffAssignee",
              headerName: "Encamp Assignee",
              flex: autoFlex.get("staffAssignee"),
              minWidth: 90,
              renderCell: ({ row }) => {
                return <StaffAssigneeCell staffAssignee={row.submission?.staffAssignee} />;
              },
            },
          ] satisfies OmnisearchGridColDef<Row>[])
        : []),
      {
        field: "submission.status",
        headerName: "Status",
        flex: autoFlex.get("submission.status"),
        minWidth: 120,
        sortable: true,
        renderCell({ row: { submission } }) {
          return (
            <SubmissionStatusCell
              status={submission?.status}
              notReportingReason={submission?.notReportingReason}
              notReportingReasonNote={submission?.notReportingReasonNote}
              isStaff={isStaff}
              hasPaymentIssue={submission?.hasPaymentIssue}
            />
          );
        },
      },
      {
        field: "waitingOn",
        headerName: "Waiting On",
        flex: autoFlex.get("waitingOn"),
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          // Names are resolved server-side so staff identities never reach a
          // customer client; they read as "Encamp Staff" here.
          const displayNames = params.row.waitingOnDisplayNames || [];

          if (displayNames.length === 0) {
            return null;
          }

          return (
            <Stack
              spacing={0}
              style={{
                paddingTop: theme.spacing(1),
                paddingBottom: theme.spacing(1),
                height: "100%",
                width: "100%",
                alignItems: "start",
                overflow: "hidden",
              }}
            >
              {displayNames.map((displayName, index) => (
                <Typography
                  key={`${displayName}-${index}`}
                  variant="body2"
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Tooltip title={displayName}>
                    <span>{displayName}</span>
                  </Tooltip>
                </Typography>
              ))}
            </Stack>
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        sortable: false,
        align: "right",
        getActions: ({ row }) => {
          const actions: any[] = [];

          // Only show "Create Submission" button for virtual events from recurring tasks
          // Materialized instances have frequency ONCE
          const isMaterializedInstance = row.frequency === "ONCE";
          if (!isMaterializedInstance && isStaff) {
            actions.push(
              <Tooltip title="Create Submission" key="createSubmission">
                <GridActionsCellItem
                  onClick={() => handleRowClick(row)}
                  label="Create Submission"
                  icon={<Add />}
                />
              </Tooltip>
            );
          }

          return actions;
        },
      },
    ],
    [autoFlex, theme, handleRowClick, isStaff]
  );

  // V2 omnisearch hook - URL is the single source of truth
  const {
    omnisearch,
    getOmnisearch, // Stable getter for callbacks (like Zustand's getState)
    setOmnisearch,
    setOmnisearchImmediate,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
  } = useOmnisearchState();

  // Chart queries now use useChartFilterKeys() which reads URL synchronously.
  // No need to sync omnisearch to a store - the URL IS the store.

  const [searchParams] = useSearchParams();
  const hasRestoredOmnisearch = useRef(false);

  // On mount, if no URL omnisearch param, restore last-used filters from localStorage.
  // NOTE: This page does NOT use the usePersistedFilters hook because the non-omnisearch
  // filters (date range, status, etc.) are driven in part by the omnisearch/table filters.
  // Persisting them separately would interfere with omnisearch defaults. New pages should
  // prefer the usePersistedFilters hook instead.
  useEffect(() => {
    if (hasRestoredOmnisearch.current) return;

    if (searchParams.has("omnisearch") || omnisearch) {
      hasRestoredOmnisearch.current = true;
      return;
    }

    if (!tenantId || !currentUserEmail) return;
    hasRestoredOmnisearch.current = true;
    const saved = localStorage.getItem(getOmnisearchStorageKey(tenantId, currentUserEmail));
    if (saved) {
      setOmnisearchImmediate(saved);
    }
  }, [searchParams, omnisearch, setOmnisearchImmediate, tenantId, currentUserEmail]);

  // Save omnisearch to sessionStorage (back navigation) and localStorage (cross-session
  // persistence). tenantId and email are read from refs so that a tenant switch does
  // not re-trigger this effect and write stale filters to the new tenant's key.
  const tenantIdRef = useRef(tenantId);
  const currentUserEmailRef = useRef(currentUserEmail);
  tenantIdRef.current = tenantId;
  currentUserEmailRef.current = currentUserEmail;
  useEffect(() => {
    const tid = tenantIdRef.current;
    const email = currentUserEmailRef.current;
    if (!tid || !email) return;
    const sessionKey = getOmnisearchSessionKey(tid, email);
    const storageKey = getOmnisearchStorageKey(tid, email);
    if (omnisearch) {
      sessionStorage.setItem(sessionKey, omnisearch);
      localStorage.setItem(storageKey, omnisearch);
    } else {
      sessionStorage.removeItem(sessionKey);
      localStorage.removeItem(storageKey);
    }
  }, [omnisearch]);

  // Facility tags filter state - derived from omnisearch (syncs to grid query)
  // Uses tag NAMES for search (like facilityTags:"California Sites")
  // Also supports facilityTags:null to filter for facilities with no tags
  // Use extractKeyValuePairs to properly handle quoted values with pipes
  const facilityTagsFilterValue = useMemo(() => {
    if (!omnisearch) return { tagNames: [], isNull: false };
    const pairs = extractKeyValuePairs(omnisearch);
    const tagsPair = pairs.find((p) => p.key === "facilityTags");
    if (!tagsPair || !tagsPair.value) return { tagNames: [], isNull: false };

    // Check if filtering for facilities with no tags
    if (tagsPair.value.length === 1 && tagsPair.value[0].toLowerCase() === "null") {
      return { tagNames: [], isNull: true };
    }

    // extractKeyValuePairs returns an array of values, properly handling quotes
    return {
      tagNames: tagsPair.value.filter((s) => s.length > 0),
      isNull: false,
    };
  }, [omnisearch]);

  const committedFacilityTagNames = facilityTagsFilterValue.tagNames;
  const isFacilityTagsNull = facilityTagsFilterValue.isNull;

  // Callback for TagsFilter component to commit changes to omnisearch
  const handleTagsFilterCommit = useCallback(
    (tagNames: string[]) => {
      if (tagNames.length === 0) {
        // None selected = remove from omnisearch
        removeOmnisearchKeyValue("facilityTags");
      } else {
        // Quote tag names that contain spaces, use pipes as separator
        const tagNamesString = tagNames
          .map((name) => (name.includes(" ") ? `"${name}"` : name))
          .join("|");
        editOmnisearchKeyValues([["facilityTags", tagNamesString]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Check if tag filter is at default (none selected)
  const isTagFilterAtDefault = committedFacilityTagNames.length === 0;

  // Status filter state - derived from omnisearch
  const statusFilterValue = extractOmnisearchValue(omnisearch, "submissionStatus");
  // Helper to get default statuses from ALL_STATUSES for consistent value format
  const defaultStatusesFromAll = useMemo(
    () => ALL_STATUSES.filter((s) => DEFAULT_STATUS_SET.has(s)),
    []
  );

  const committedStatuses = useMemo(() => {
    // No filter in omnisearch = use default statuses (excludes completed)
    // Return constant directly to maintain stable reference
    if (statusFilterValue === undefined || statusFilterValue === "") {
      return defaultStatusesFromAll;
    }
    // "none" marker = no statuses selected (show nothing)
    if (statusFilterValue === "none") {
      return EMPTY_ARRAY;
    }
    // Support both pipe (|) and comma (,) separators for compatibility with data grid filters
    // Filter out invalid status values to prevent GraphQL enum validation errors
    const parsedStatuses = statusFilterValue
      .split(/[|,]/)
      .filter((s) => s.length > 0 && ALL_STATUSES.includes(s as SubmissionStatus));
    // If all values were invalid, fall back to defaults
    return parsedStatuses.length > 0 ? parsedStatuses : defaultStatusesFromAll;
  }, [statusFilterValue, defaultStatusesFromAll]);

  // Callback for StatusFilter component to commit changes to omnisearch
  const handleStatusFilterCommit = useCallback(
    (statuses: string[]) => {
      if (statuses.length === 1 && statuses[0] === "none") {
        // No statuses selected
        editOmnisearchKeyValues([["submissionStatus", "none"]]);
      } else if (statuses.length === 0) {
        // Matches defaults - remove from omnisearch to let dynamic default apply
        removeOmnisearchKeyValue("submissionStatus");
      } else {
        // Custom selection
        editOmnisearchKeyValues([["submissionStatus", statuses.join("|")]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Check if status filter is at default (based on committed values)
  const isStatusFilterAtDefault = useMemo(() => {
    if (committedStatuses.length !== defaultStatusesFromAll.length) return false;
    return defaultStatusesFromAll.every((s) => committedStatuses.includes(s));
  }, [committedStatuses, defaultStatusesFromAll]);

  // ═══════════════════════════════════════════════════════════════════════════
  // DERIVED CHART SELECTION - computed from omnisearch, not stored separately
  // A chart bar is "selected" (highlighted) when the filter is a single value
  // ═══════════════════════════════════════════════════════════════════════════

  // Status: selected if filter is a single status (not pipe-separated, not default)
  const selectedChartStatus = useMemo(() => {
    if (!statusFilterValue) return null;
    // Multiple values = no single selection
    if (statusFilterValue.includes("|") || statusFilterValue.includes(",")) return null;
    // Default statuses = no selection
    if (defaultStatusesFromAll.join("|") === statusFilterValue) return null;
    return statusFilterValue as SubmissionStatus;
  }, [statusFilterValue, defaultStatusesFromAll]);

  // Extract readyForAction email value from omnisearch
  const readyForActionEmail = extractOmnisearchValue(omnisearch, "readyForAction");
  const readyForAction = !!readyForActionEmail;
  const filedSubmissionsDigest = extractOmnisearchValue(omnisearch, "filedSubmissionsDigest");
  const isFiledSubmissionsDigest = filedSubmissionsDigest === "true";

  // "In my queue": I'm a not-yet-complete reviewer, regardless of whose turn it is
  const queuedForMe = extractOmnisearchValue(omnisearch, "queuedForMe") === "true";

  // Staff-only view filters
  const needsMyAttention = extractOmnisearchValue(omnisearch, "needsMyAttention") === "true";
  const peerReviewQueue = extractOmnisearchValue(omnisearch, "peerReviewQueue") === "true";

  type ViewByValue =
    | "all"
    | "readyForAction"
    | "queuedForMe"
    | "needsMyAttention"
    | "peerReviewQueue";

  const viewByValue = useMemo((): ViewByValue => {
    if (readyForAction) return "readyForAction";
    if (queuedForMe) return "queuedForMe";
    if (needsMyAttention) return "needsMyAttention";
    if (peerReviewQueue) return "peerReviewQueue";
    return "all";
  }, [readyForAction, queuedForMe, needsMyAttention, peerReviewQueue]);

  const handleViewByChange = useCallback(
    (event: SelectChangeEvent<ViewByValue>) => {
      const value = event.target.value as ViewByValue;
      let cleaned = omnisearch
        .replace(/\b(readyForAction|queuedForMe|needsMyAttention|peerReviewQueue):\S+/g, "")
        .replace(/\s+/g, " ")
        .trim();
      switch (value) {
        case "readyForAction":
          if (currentUserEmail) {
            cleaned = `${cleaned} readyForAction:${currentUserEmail}`.trim();
          }
          break;
        case "queuedForMe":
          cleaned = `${cleaned} queuedForMe:true`.trim();
          break;
        case "needsMyAttention":
          cleaned = `${cleaned} needsMyAttention:true`.trim();
          break;
        case "peerReviewQueue":
          cleaned = `${cleaned} peerReviewQueue:true`.trim();
          break;
      }
      setOmnisearchImmediate(cleaned);
    },
    [omnisearch, currentUserEmail, setOmnisearchImmediate]
  );

  // Static defaultSearch - always includes defaults
  // The omnisearch backend merges duplicate keys, with later values taking precedence
  // So if user sets submissionStatus:X in omnisearch, it overrides the default here
  // Note: status:ACTIVE|OVERDUE was removed because Task.completedAt is not automatically
  // set by the submission pipeline, making it redundant with submissionStatus filtering
  const defaultSearch = useMemo(() => {
    if (isFiledSubmissionsDigest) {
      return `tenantId:${tenantId}`;
    }

    return `tenantId:${tenantId} submissionStatus:${defaultStatusesFromAll.join(
      "|"
    )} dueDate:${DEFAULT_DUE_DATE}`;
  }, [tenantId, defaultStatusesFromAll, isFiledSubmissionsDigest]);

  // Memoized callbacks for OmnisearchDataGrid to prevent unnecessary re-renders
  const getItems = useCallback(
    (data: SubmissionVirtualEventsQuery) => data.submissionVirtualEvents.items,
    []
  );

  const getCount = useCallback(
    (data: SubmissionVirtualEventsQuery) => data.submissionVirtualEvents.count ?? 0,
    []
  );

  const handleGridRowClick = useCallback(
    ({ row }: GridRowParams<Row>) => handleRowClick(row),
    [handleRowClick]
  );

  // Memoize commandButtons array
  // Uses StableExportButton which reads omnisearch lazily via getOmnisearch
  // Uses SubmissionBulkActionsMenu which subscribes to selection via context
  const commandButtons = useMemo(
    () => [
      <StableExportButton
        key="export"
        tenantId={tenantId}
        defaultSearch={defaultSearch}
        getOmnisearch={getOmnisearch}
      />,
      <BulkDownloadSubmissionsButton
        key="download-pdfs"
        tenantId={tenantId}
        defaultSearch={defaultSearch}
        getOmnisearch={getOmnisearch}
      />,
      ...(isStaff
        ? [
            <SubmissionBulkActionsMenu key="bulk-actions" />,
            <Button
              key="create-submission"
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddSubmission}
              color="staff"
              sx={{ whiteSpace: "nowrap" }}
            >
              Create Submission
            </Button>,
            <Button
              key="bulk-create-submissions"
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenBulkCreate}
              color="staff"
              sx={{ whiteSpace: "nowrap" }}
            >
              Bulk Create
            </Button>,
          ]
        : []),
    ],
    [tenantId, defaultSearch, getOmnisearch, isStaff, handleAddSubmission, handleOpenBulkCreate]
  );

  // Memoize additionalFilterColumns - mostly static but has presentation functions
  const additionalFilterColumns = useMemo<FilterKey[]>(
    () => [
      {
        header: "Facility ID",
        key: "customerFacilityId",
        filterKeyType: "text",
      },
      {
        header: "EPA ID",
        key: "epaId",
        filterKeyType: "text",
      },
      {
        header: "Facility",
        key: "facilityId",
        filterKeyType: "wasteMultipleFacility",
      },
      {
        header: "Attachment Name",
        key: "documents",
        filterKeyType: "text",
      },
      {
        header: "Submission Type",
        key: "submissionType",
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(SubmissionType),
      },
      {
        header: "Reviewer",
        key: "reviewers",
        filterKeyType: isStaff ? "multipleUser" : "multipleCustomerUser",
        nullCheckboxLabel: "No Reviewer",
      },
      {
        header: "Signer",
        key: "signer",
        filterKeyType: isStaff ? "multipleUser" : "multipleCustomerUser",
        nullCheckboxLabel: "No Signer",
      },
      {
        header: "Waiting On",
        key: "waitingOn",
        filterKeyType: isStaff ? "multipleUser" : "multipleCustomerUser",
      },
      {
        header: "In Queue",
        key: "queuedReviewer",
        filterKeyType: isStaff ? "multipleUser" : "multipleCustomerUser",
      },
      {
        header: "Is Revision",
        key: "isRevision",
        filterKeyType: "boolean",
      },
      ...(isStaff
        ? ([
            {
              header: "Encamp Assignee",
              key: "staffAssignee",
              filterKeyType: "text",
            },
            {
              // Internal workflow state (rejected, needs correction, blocked…) —
              // staff-only, like the other Encamp internals on this grid.
              header: "Encamp Status",
              key: "encampStatus",
              filterKeyType: "enumAutocomplete",
              enumValues: Object.values(SubmissionEncampStatus),
            },
            {
              header: "Payment issue",
              key: "paymentIssue",
              filterKeyType: "boolean",
            },
          ] satisfies FilterKey[])
        : []),
    ],
    [isStaff]
  );

  const excludeFilterColumns = useMemo(
    () => [
      "submissionType",
      "dueDate",
      "status",
      "documents",
      "reviewers",
      "signer",
      ...(isStaff ? ["staffAssignee"] : []),
      "facilityName",
      "submission.submissionType",
      "facilityId",
      "deadline",
      "facilities",
      "epaIds",
      "submission.status",
      "waitingOn",
    ],
    [isStaff]
  );

  // Chart click handlers moved after derived chart selection values (see below)

  // Handler for resetting all filters to default
  const handleResetFilters = useCallback(() => {
    // Clear all filters from omnisearch at once - using setOmnisearch directly
    // to avoid stale state issues with multiple removeOmnisearchKeyValue calls
    const keysToRemove = new Set([
      "submissionStatus",
      "dueDate",
      "states",
      "generatorStatus",
      "epaId",
      "readyForAction",
      "queuedForMe",
      "queuedReviewer",
      "needsMyAttention",
      "peerReviewQueue",
      "facilityTags",
      "isRevision",
    ]);

    // Parse omnisearch tokens respecting quotes
    const tokens: string[] = [];
    let current = "";
    let inQuotes = false;
    let quoteChar = "";
    for (let i = 0; i < omnisearch.length; i++) {
      const char = omnisearch[i];
      if (!inQuotes && (char === '"' || char === "'")) {
        inQuotes = true;
        quoteChar = char;
        current += char;
      } else if (inQuotes && char === quoteChar) {
        inQuotes = false;
        quoteChar = "";
        current += char;
      } else if (!inQuotes && char === " ") {
        if (current.trim()) {
          tokens.push(current.trim());
          current = "";
        }
      } else {
        current += char;
      }
    }
    if (current.trim()) tokens.push(current.trim());

    // Filter out tokens with keys to remove
    const filteredTokens = tokens.filter((token) => {
      const colonIndex = token.indexOf(":");
      if (colonIndex > 0) {
        const key = token.substring(0, colonIndex);
        return !keysToRemove.has(key);
      }
      return true; // Keep non-key-value tokens
    });

    setOmnisearch(filteredTokens.join(" "));

    // Chart selections are derived from omnisearch, no need to reset separately
  }, [omnisearch, setOmnisearch]);

  // State filter state - derived from omnisearch
  const statesFilterValue = extractOmnisearchValue(omnisearch, "states");

  const committedStates = useMemo(() => {
    // No filter = all states selected
    // Return constant directly to maintain stable reference
    if (statesFilterValue === undefined || statesFilterValue === "") {
      return US_STATES;
    }
    // "none" marker = no states selected
    if (statesFilterValue === "none") {
      return EMPTY_ARRAY;
    }
    // Support both pipe (|) and comma (,) separators for compatibility with data grid filters
    return statesFilterValue.split(/[|,]/).filter((s) => s.length > 0);
  }, [statesFilterValue]);

  // Callback for StateFilter component to commit changes to omnisearch
  const handleStateFilterCommit = useCallback(
    (states: string[]) => {
      if (states.length === 0) {
        // None selected
        editOmnisearchKeyValues([["states", "none"]]);
      } else if (states.length === US_STATES.length) {
        // All selected = remove from omnisearch to let default apply
        removeOmnisearchKeyValue("states");
      } else {
        // Custom selection
        editOmnisearchKeyValues([["states", states.join("|")]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Check if state filter is at default (all states selected)
  const isStateFilterAtDefault = committedStates.length === US_STATES.length;

  // Derived chart state selection (for highlighting clicked bar)
  const selectedChartState = useMemo(() => {
    if (!statesFilterValue) return null;
    if (statesFilterValue.includes("|") || statesFilterValue.includes(",")) return null;
    if (statesFilterValue === "none") return null;
    return statesFilterValue;
  }, [statesFilterValue]);

  // Generator status filter state - derived from omnisearch
  const generatorStatusFilterValue = extractOmnisearchValue(omnisearch, "generatorStatus");
  const committedGeneratorStatuses = useMemo(() => {
    // No filter = all generator statuses selected
    // Return constant directly to maintain stable reference
    if (generatorStatusFilterValue === undefined || generatorStatusFilterValue === "") {
      return ALL_GENERATOR_STATUSES;
    }
    // "none" marker (legacy) = treat as selecting only NONE generator status
    // This prevents errors from sending invalid enum value to backend
    if (generatorStatusFilterValue === "none") {
      return [RegulatoryStatusOption.None];
    }
    // Support both pipe (|) and comma (,) separators for compatibility with data grid filters
    // Filter out invalid generator status values to prevent GraphQL enum validation errors
    const parsedStatuses = generatorStatusFilterValue
      .split(/[|,]/)
      .filter(
        (s) => s.length > 0 && ALL_GENERATOR_STATUSES.includes(s as RegulatoryStatusOption)
      ) as RegulatoryStatusOption[];
    // If all values were invalid, fall back to defaults
    return parsedStatuses.length > 0 ? parsedStatuses : ALL_GENERATOR_STATUSES;
  }, [generatorStatusFilterValue]);

  // Callback for GeneratorStatusFilter component to commit changes to omnisearch
  const handleGeneratorStatusFilterCommit = useCallback(
    (statuses: RegulatoryStatusOption[]) => {
      if (statuses.length === 0) {
        // None selected = treat as selecting only NONE generator status
        editOmnisearchKeyValues([["generatorStatus", RegulatoryStatusOption.None]]);
      } else if (statuses.length === ALL_GENERATOR_STATUSES.length) {
        // All selected = remove from omnisearch to let default apply
        removeOmnisearchKeyValue("generatorStatus");
      } else {
        // Custom selection
        editOmnisearchKeyValues([["generatorStatus", statuses.join("|")]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Check if generator status filter is at default (all selected)
  const isGeneratorStatusFilterAtDefault =
    committedGeneratorStatuses.length === ALL_GENERATOR_STATUSES.length;

  // Derived chart generator status selection (for highlighting clicked bar)
  const selectedChartGeneratorStatus = useMemo(() => {
    if (!generatorStatusFilterValue) return null;
    if (generatorStatusFilterValue.includes("|") || generatorStatusFilterValue.includes(","))
      return null;
    return generatorStatusFilterValue;
  }, [generatorStatusFilterValue]);

  // ═══════════════════════════════════════════════════════════════════════════
  // CHART CLICK HANDLERS - defined after derived chart selection values
  // ═══════════════════════════════════════════════════════════════════════════

  const handleChartStatusClick = useCallback(
    (status: SubmissionStatus) => {
      if (selectedChartStatus === status) {
        removeOmnisearchKeyValue("submissionStatus");
      } else {
        editOmnisearchKeyValues([["submissionStatus", status]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, selectedChartStatus]
  );

  const handleChartStateClick = useCallback(
    (state: string) => {
      if (selectedChartState === state) {
        removeOmnisearchKeyValue("states");
      } else {
        editOmnisearchKeyValues([["states", state]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, selectedChartState]
  );

  const handleChartGeneratorStatusClick = useCallback(
    (status: string) => {
      if (selectedChartGeneratorStatus === status) {
        removeOmnisearchKeyValue("generatorStatus");
      } else {
        editOmnisearchKeyValues([["generatorStatus", status]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, selectedChartGeneratorStatus]
  );

  // Date range filter state - derived from omnisearch hook state
  // IMPORTANT: Use UTC zone when parsing date-only strings to match how the backend
  // resolver parses dates. Without this, Luxon interprets date-only strings in local
  // timezone, causing events at midnight UTC to be counted differently by the charts
  // vs the datagrid.
  const dueDateValue = extractOmnisearchValue(omnisearch, "dueDate");
  const [dueDateStart, dueDateEnd] = useMemo(() => {
    // No filter in omnisearch = use default (30 days back through 60 days forward)
    if (!dueDateValue) {
      return [
        DateTime.fromISO(DEFAULT_DUE_DATE_START, { zone: "utc" }),
        DateTime.fromISO(DEFAULT_DUE_DATE_END, { zone: "utc" }),
      ];
    }
    const parts = dueDateValue.split("..");
    const start = parts[0] ? DateTime.fromISO(parts[0], { zone: "utc" }) : null;
    const end = parts[1] ? DateTime.fromISO(parts[1], { zone: "utc" }) : null;
    return [start, end];
  }, [dueDateValue]);

  // Check if due date filter is at default (30 days back through 60 days forward)
  const isDueDateFilterAtDefault = useMemo(() => {
    if (!dueDateStart || !dueDateEnd) return false;
    const defaultStart = DateTime.fromISO(DEFAULT_DUE_DATE_START, {
      zone: "utc",
    });
    const defaultEnd = DateTime.fromISO(DEFAULT_DUE_DATE_END, { zone: "utc" });
    return (
      dueDateStart.toISODate() === defaultStart.toISODate() &&
      dueDateEnd.toISODate() === defaultEnd.toISODate()
    );
  }, [dueDateStart, dueDateEnd]);

  // Check if ALL filters are at their default values
  const areFiltersAtDefault = useMemo(() => {
    return (
      isStatusFilterAtDefault &&
      isStateFilterAtDefault &&
      isGeneratorStatusFilterAtDefault &&
      isTagFilterAtDefault &&
      isDueDateFilterAtDefault &&
      !readyForAction &&
      !needsMyAttention &&
      !peerReviewQueue
    );
  }, [
    isStatusFilterAtDefault,
    isStateFilterAtDefault,
    isGeneratorStatusFilterAtDefault,
    isTagFilterAtDefault,
    isDueDateFilterAtDefault,
    readyForAction,
    needsMyAttention,
    peerReviewQueue,
  ]);

  const handleDueDateRangeChange = useCallback(
    (newValue: DateRange<DateTime>) => {
      const [start, end] = newValue;
      if (!start && !end) {
        // Cleared - remove from omnisearch (dynamic default will apply)
        removeOmnisearchKeyValue("dueDate");
      } else {
        const startStr = start?.toISODate() ?? "";
        const endStr = end?.toISODate() ?? "";
        const newDateRange = `${startStr}..${endStr}`;
        // If selecting exactly default date range, remove from omnisearch
        if (newDateRange === DEFAULT_DUE_DATE) {
          removeOmnisearchKeyValue("dueDate");
        } else {
          editOmnisearchKeyValues([["dueDate", newDateRange]]);
        }
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Clear bulk selections when filtering changes
  useEffect(() => {
    // Use apiRef to clear selection without causing re-renders
    const api = apiRef?.current;
    if (api && api.getSelectedRows().size > 0) {
      api.setRowSelectionModel({ type: "include", ids: new Set() });
    }
  }, [omnisearch, apiRef]);

  // Chart selection is now DERIVED from omnisearch (see useMemo above)
  // No useEffects needed to sync - the derived values update automatically

  // Simple static title - filter context is shown in the Status dropdown
  const titleFilter = "Submissions";

  // Custom empty state is handled by SubmissionsEmptyStateOverlay via slots
  // The overlay uses hooks internally to read omnisearch state (zero parent re-renders)

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        name: "Submission Info",
        fields: [
          "submissionStatus",
          "submissionType",
          "encampStatus",
          "title",
          "isRevision",
          "notes",
          "documents",
        ],
      },
      {
        name: "Location",
        fields: ["facilityId", "states", "generatorStatus", "epaId", "customerFacilityId"],
      },
      {
        name: "Tags",
        fields: ["facilityTags"],
      },
      {
        name: "Assignment",
        fields: [
          "reviewers",
          "signer",
          ...(isStaff ? ["staffAssignee"] : []),
          "waitingOn",
          "queuedReviewer",
        ],
      },
    ],
    [isStaff]
  );

  return (
    <Stack sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <WasteStepperAnnouncement
        phase={announcementPhase}
        isPending={announcementIsPending}
        dismissModal={dismissAnnouncementModal}
        clickPrimaryCta={clickAnnouncementPrimaryCta}
      />
      <Stack
        id="submission-filter-header"
        paddingX={2}
        paddingY={1.5}
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderBottomColor: "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1.5}
          flexWrap="wrap"
        >
          <Stack direction="row" gap={1.5} alignItems="flex-end" flexWrap="wrap">
            {/* Status Filter */}
            <StatusFilter
              committedStatuses={committedStatuses}
              defaultStatuses={defaultStatusesFromAll}
              onCommit={handleStatusFilterCommit}
            />
            {/* View By Filter */}
            <FormControl variant="standard" size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="view-by-label">View by</InputLabel>
              <Select<ViewByValue>
                labelId="view-by-label"
                value={viewByValue}
                onChange={handleViewByChange}
                renderValue={(v) => {
                  switch (v) {
                    case "readyForAction":
                      return "Ready for next action";
                    case "queuedForMe":
                      return "In my queue";
                    case "needsMyAttention":
                      return "Needs my attention";
                    case "peerReviewQueue":
                      return "Peer review queue";
                    default:
                      return "All";
                  }
                }}
              >
                <MenuItem value="all">
                  <Radio checked={viewByValue === "all"} size="small" sx={{ p: 0, mr: 1 }} />
                  All
                </MenuItem>
                <MenuItem value="readyForAction">
                  <Radio
                    checked={viewByValue === "readyForAction"}
                    size="small"
                    sx={{ p: 0, mr: 1 }}
                  />
                  Ready for next action
                </MenuItem>
                <MenuItem value="queuedForMe">
                  <Radio
                    checked={viewByValue === "queuedForMe"}
                    size="small"
                    sx={{ p: 0, mr: 1 }}
                  />
                  In my queue
                </MenuItem>
                {isStaff && (
                  <MenuItem value="needsMyAttention">
                    <Radio
                      checked={viewByValue === "needsMyAttention"}
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                    />
                    Needs my attention
                  </MenuItem>
                )}
                {isStaff && (
                  <MenuItem value="peerReviewQueue">
                    <Radio
                      checked={viewByValue === "peerReviewQueue"}
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                    />
                    Peer review queue
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Generator Status Filter */}
            <GeneratorStatusFilter
              committedGeneratorStatuses={committedGeneratorStatuses}
              onCommit={handleGeneratorStatusFilterCommit}
            />

            {/* State Filter */}
            <StateFilter committedStates={committedStates} onCommit={handleStateFilterCommit} />

            {/* Due Date Range Filter */}
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateRangePicker
                value={[dueDateStart, dueDateEnd]}
                onChange={handleDueDateRangeChange}
                localeText={{ start: "Due Start", end: "Due End" }}
                slotProps={{
                  popper: { placement: "bottom-end" },
                  shortcuts: {
                    items: datePickerShortcuts,
                  },
                  textField: {
                    variant: "standard",
                    size: "small",
                    sx: { width: 105 },
                    inputProps: {
                      autoComplete: "off",
                      "data-lpignore": "true",
                      "data-form-type": "other",
                      "data-1p-ignore": "true",
                    },
                  },
                }}
                slots={{
                  field: MultiInputDateRangeField,
                  shortcuts: DateShortcutChips,
                }}
              />
            </LocalizationProvider>

            {/* Tags Filter and Ready Toggle - grouped together for alignment */}
            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              sx={{ alignSelf: "flex-end", mb: 0.5 }}
            >
              {/* Tags Filter */}
              <TagsFilter
                allFacilityTags={allFacilityTags}
                committedFacilityTagNames={committedFacilityTagNames}
                isFacilityTagsNull={isFacilityTagsNull}
                isFetchingMoreTags={isFetchingMoreFacilityTags}
                onCommit={handleTagsFilterCommit}
                onScroll={handleFacilityTagsScroll}
              />

              {/* Reset filters button - always rendered to prevent layout shift, hidden when at default */}
              <Button
                onClick={handleResetFilters}
                variant="text"
                size="small"
                sx={{
                  visibility: areFiltersAtDefault ? "hidden" : "visible",
                }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>
          <Stack direction="row" gap={0.5} alignItems="center">
            <Button onClick={handleToggle} variant="text" size="small">
              {statsOpen ? "Hide Stats" : "Show Stats"}
            </Button>
            {documentStepperEnabled && (
              <Tooltip title="Show me around again">
                <IconButton
                  size="small"
                  color="secondary"
                  aria-label="Show me around again"
                  onClick={reopenAnnouncementModal}
                >
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Stack>
      <StatsSection
        statsOpen={statsOpen}
        onStatusClick={handleChartStatusClick}
        selectedStatus={selectedChartStatus}
        onStateClick={handleChartStateClick}
        selectedState={selectedChartState}
        onGeneratorStatusClick={handleChartGeneratorStatusClick}
        selectedGeneratorStatus={selectedChartGeneratorStatus}
      />
      <Stack
        id="submission-list"
        p={2}
        sx={{
          background: theme.palette.grey50.main,
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <GridSection
          title={titleFilter}
          description="General waste reporting submissions. Use the search and filters to find specific submissions."
          persistenceKey={omnisearchPersistenceKey}
          columns={columns}
          dataQuery={SUBMISSION_VIRTUAL_EVENTS_QUERY}
          defaultSearch={defaultSearch}
          getItems={getItems}
          getCount={getCount}
          checkboxSelection={isStaff}
          onRowClick={handleGridRowClick}
          getRowLink={getRowHref}
          commandButtons={commandButtons}
          initialColumnVisibilityModel={INITIAL_COLUMN_VISIBILITY_MODEL}
          mobileColumnVisibilityModel={MOBILE_COLUMN_VISIBILITY_MODEL}
          additionalFilterKeys={additionalFilterColumns}
          excludeFilterColumns={excludeFilterColumns}
          filterGroups={filterGroups}
          tenantId={tenantId}
        />
      </Stack>

      <BulkCreateNewSubmissionsDialog
        open={showBulkCreateDialog}
        onClose={() => setShowBulkCreateDialog(false)}
        onSuccess={handleBulkCreateSuccess}
      />

      {/* Mounted only while open so the form/method/facility/due-date state
          resets on every close path rather than persisting into the next open. */}
      {showTypeDialog && (
        <CreateSubmissionTypeDialog
          open={showTypeDialog}
          onClose={() => setShowTypeDialog(false)}
          onCreateStepper={handleCreateStepper}
          creating={creatingStepper}
          showStepperOption={stepperEnabled}
          showPaperFormOption={documentStepperEnabled}
        />
      )}
    </Stack>
  );
}
